import React from "react";
import styled from "@emotion/styled";
import BackHeader from "@components/header/BackHeader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const Container = styled.div(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: "white",
}));

const ContentWrapper = styled.div(({ theme }) => ({
  padding: "2rem",

  u: {
    textDecoration: "underline",
    fontWeight: "normal",
  },

  h1: {
    fontSize: "2.4rem",
    fontWeight: 600,
    marginBottom: "2rem",
  },

  h2: {
    fontSize: "2rem",
    fontWeight: 600,
    marginBottom: "1.6rem",
    marginTop: "3.2rem",
  },

  p: {
    fontSize: "1.4rem",
    lineHeight: "2.4rem",
    marginBottom: "1.6rem",
  },

  "ul, ol": {
    paddingLeft: "2rem",
    marginBottom: "1.6rem",
  },

  li: {
    fontSize: "1.4rem",
    lineHeight: "2.4rem",
    marginBottom: "0.8rem",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "2rem",

    "th, td": {
      border: `1px solid ${theme.color.border_gray}`,
      padding: "1.2rem",
      fontSize: "1.4rem",
      lineHeight: "2rem",
    },

    th: {
      backgroundColor: theme.color.light_gray,
      fontWeight: 500,
    },
  },
}));

const privacyPolicyMarkdown = `
# 샤카라카 개인정보처리방침

주식회사 아울소싸이어티(이하 “회사”라고 함)의 샤카라카(이하 “서비스” 또는 “샤카라카”라고 함)는 "정보통신망 이용촉진 및 정보보호에 관한 법률", “개인정보보호법”, "통신비밀보호법", "전기통신사업법" 및 “전자상거래 등에서의 소비자 보호에 관한 법률” 등 정보통신서비스제공자가 준수하여야 할 관련 법령상의 개인정보보호 규정을 준수하며, 관련 법령에 의거한 개인정보처리방침을 정하여 이용자 권익 보호에 최선을 다하겠습니다. 회사는 이용자의 개인정보를 [개인정보의 수집목적]에서 고지한 범위 내에서 사용하며, 이용자의 사전 동의 없이는 동 범위를 초과하여 이용하거나 원칙적으로 이용자의 개인정보를 외부에 제공 또는 위탁하지 않습니다. 다만, 아래의 경우에는 예외로 합니다.

- 이용자가 사전에 동의한 경우(이용자가 사전에 동의한 경우란, 서비스 이용 등을 위하여 이용자가 자발적으로 자신의 개인정보를 제3자에게 제공하는 것에 동의하는 것을 의미합니다.)
- 법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우

이러한 경우에도, 회사는 이용자에게 (1) 개인정보를 제공받는 자 (2) 그의 이용목적 (3) 개인정보의 보유 및 이용기간을 사전에 고지하고 이에 대해 명시적·개별적으로 동의를 얻습니다. 이와 같은 모든 과정에 있어서 회사는 이용자의 의사에 반하여 추가적인 정보를 수집하거나, 동의의 범위를 벗어난 정보를 제3자와 공유하지 않습니다.

## 1. 개인정보의 수집 및 처리 목적

샤카라카의 개인정보 수집은 이용자에게 안전하고 최적화된, 효율적인 맞춤화된 브라우징 경험을 제공하는
것을 주요 목적으로 합니다. 당사는 다음과 같은 목적으로 귀하의 개인정보를 이용합니다:

- 이용자 계정을 관리(구독, 구독취소, 차단 등)하기 위한 목적(목적 1번);
- 샤카라카와의 업무를 위해 애플리케이션을 관리하기 위한 목적(목적 2번);
- 당사의 서비스 및 (샤카라카가 대리인으로서 행위하는 경우) 당사 파트너의 서비스를 제공하기 위한 목적(목적 3번);
- 판매된 상품을 관리하고, 특히 품질관리를 보증하기 위한 목적(목적 4번);
- 거래, 주문 및 정산을 처리하기 위한 목적(목적 5번);
- 당사의 고객관계(VIP 고객과의 관계 포함) 및 잠재고객을 관리하기 위한 목적(특히 본 개인정보처리방침 및 쿠키와 당사 웹사이트의 이용약관 등과 관련하여 중요한 정보의 제공 포함)(목적 6번);
- 이용자가 기대하는 서비스를 개발, 개선 및 제공하기 위해 귀하(귀하의 요구, 관심 등)를 더 잘 알기 위한 목적(목적 7번);
- 문제나 클레임을 해결하기 위한 목적(목적 8번);
- 당사의 서비스 및 콘텐츠를 맞춤화, 평가 및 개선하기 위해 통계를 구축하고 설문조사를 수행하기 위한 목(목적 9번);
- 이용자의 동의를 취득하여 타겟 마케팅 및/또는 프로모션용 제안을 통해 당사의 서비스 및 당사 파트너사의 서비스에 대해 귀하에게 알리기 위한 목적(목적 10번);
- 잠재적으로 금지되고 위법한 활동을 방지, 감지 및 조사하고, 당사의 일반판매·이용약관을 집행하기 위한 목적(목적 11번);
- 공공 세무·사법당국의 관리 및 조사 임무에 협력할 샤카라카의 법적 의무 등, 당사의 법적·규제의무를 준수하기 위한 목적(목적 12번);
- 제4항 “제공받는 자”에 언급된 정보수령인에게 개인정보를 이전하기 위한 목적(목적 13번);
- 당사가 당사 고객과 체결할 수 있는 계약을 행정적· 재무적으로 관리하기 위한 목적(“목적 14번”);
- 샤카라카가 이용자들에게 제공하는 서비스의 품질을 개선하기 위한 목적:
개인 채팅이나 댓글에서 이용자들 간에 전송하는 메시지를 관리하고, 당사의 이용약관을 준수하도록 하고, 
요청하지 않은 콘텐츠, 악성 컴퓨터 프로그램을 감지, 커뮤니케이션 기능을 개선, 이용자가 신고한 잠재적 부정행동을 식별, 거래를 모니터링, 사기 조사를 수행, 고객지원을 보장, 이용자들 간의 클레임이나 분쟁에 대한 정보를 취득하고 이 웹사이트 및 그 기능에 대한 통계를 분석합니다(목적 15번).

## 2. 개인정보 저장

정보보호법상 요구되는 바에 따라, 이 웹사이트를 통해 수집 및 처리되는 모든 개인정보는 다음과 같이 적법하게
확인된 법적 근거에 따라 수집 및 처리됩니다:

- 목적 1번, 3번, 4번, 6번, 7번, 8번, 9번, 10번, 11번 및 13번은 이 웹사이트 및 제공된 서비스와 관련하여 
샤카라카나 제3자(당사의 사업파트너 등)의 적법한 사업목적상 필요하고, 해당 정보의 수집 및 처리가 어떠한 방식으로도 이용자의 이익이나 기본적 권리를 방해하지 않음을 명확히 합니다.
- 목적 10번은 (해당되는 경우) 당사가 사전에 이용자의 동의를 받았음을 보여줍니다.
- 목적 2번은 이용자의 요청에 따라 계약 체결이나 이행을 위해 필요합니다.
- 목적 3번(샤카라카가 자체 서비스를 제공하는 경우), 목적 5번(샤카라카의 일반판매약관을 이행할 목적) 
및 목적 14번은 샤카라카가 이용자와의 구속력이 있는 계약을 이행하는 것을 전제로 합니다.
- 목적 12번은 샤카라카에게 적용되는 법적 의무의 이행을 위해 필요합니다.
- 목적13번은 이용자가 당사자인 계약을 이행하기 위해 필요하거나, 이용자가 서명한 계약을 관리· 실행함에 있어 샤카라카의 적법성을 기반으로 합니다.
- 목적 15번은 샤카라카 및 제3자의 적법한 이익을 보호하는 데 필요합니다. 당사가 수집 및/또는 처리할 수 있는 민감정보는 이용자의 명시적인 동의에 따라서만 수집 및 처리되며, 이용자가 이에 대해 알지 못하는 경우 수집되거나 처리되지 않습니다. 어떠한 경우에도 당사는 이용자가 반드시 필요한 정보만을 당사에 제공하여 정보보호법과 개인정보보호법상 부과된 정보 최소화 원칙을 준수할 것을 권장합니다.
- 이용자의 개인정보가 이용자의 동의를 기반으로 처리되는 경우, 이용자는 언제라도 이용자의 동의를 철회할 수 있고, 법적 근거를 기반으로 개인정보가 수집 및 처리되도록 합니다.

## 3. 개인정보 처리 및 보유기간

샤카라카는 이용자의 개인정보가 수집된 목적을 이행하는 데 필요한 한, 이용자의 개인정보를 식별가능한 형식으로 보유합니다. 

따라서, 기본적으로:

- 이 웹사이트에서 이용자의 브라우징 세션과 관련하여 자동 수집된 정보는 이용자의 다음 방문을 고려하여 보유되고, 브라우저 캐시를 삭제해서만 수동으로 삭제되며(쿠키 섹션 참조);
- 이용자가 당사에 전달한 이 웹사이트상 이용자의 인터랙션과 관련되고 상업적 관계를 관리(계약 체결, 예측)할 목적으로 사용되는 정보는 상업적 관계상 필요한 기간에 3년을 더한 기간 동안 보유되며(본 방침의 기타 규정을 전제로 함);
- 잠재고객에 관한 정보는 수집일이나 잠재고객과의 최종 계약으로부터 3년 동안 보유되며;
- 이 웹사이트에 등록된 이용자들의 정보는 계정이 삭제될 때까지 보유되며;
- 계약 체결과 관련하여 수집된 정보는 전체 이행기간 동안 보유되고, 해당 법정시효기간 동안 보관되며;
- 법정 납세의무와 관련하여 샤카라카가 수집한 정보는 해당 정보가 연간 단위로 조세당국에 전송될 때까지 보유되고, 해당 부과제척기간 동안 보관되며;
- 통계 정보 등 기타 정보는 이용자를 알아볼수없는 형식으로 보유됩니다.

이용자는 사망 이후의 개인정보의 저장, 삭제 및 공개에 대한 가이드라인을 설정할 수 있습니다. 이를 위해, hello@owlsociety.kr 로 연락주시기 바랍니다. 그러한 가이드라인이 없는 경우, 이용자의 상속인이 더 이른 시점에 삭제를 요청하지 않는 한, 이용자의 개인정보는 상기 조항에 따라 보유됩니다.

## 4. 개인정보의 제3자 제공

샤카라카는 고객의 개인정보를 서비스이용약관, 개인정보처리방침의 개인정보의 처리 목적에서 알린 범위 내에서 사용하며, 이 범위를 초과하여 이용하거나 타인 또는 다른 기업·기관에 제공하지 않습니다. 단, 고객의 동의가 있거나 법령에 의하여 정해진 절차에 따라 정보를 요청받은 경우는 예외로 하며, 이 경우 주의를 기울여 개인정보를 이용 및 제공할 수 있습니다.

- 제공받는 자
회사가 수집하는 개인정보를 다음에 해당하는 자와 공유할 수 있습니다:
(i) 알 필요가 있는 회사의 내부 부서;
(ii) 특히 해외결제 및 거래 시, 특정 정보에 직·간접적으로 접근하여야 하는 제3자들;
(iii) 광고 플랫폼 및 사업파트너(이 경우, 귀하의 동의를 받음);
(iv) 다음을 포함한 당사의 서비스제공자들:
     a) 배송 및 지급결제 서비스제공자,
     b) CRM 서비스제공자,
     c) 상품검증 서비스제공자,
     d) IT 서비스제공자;
(v) 이용자가 구매하는 물품을 판매자가 직접 이용자에게 발송하는 경우, 판매자;
(vi) (경우에 따라) 구매자가 판매자로부터 직접 구매한 물품을 판매자에게 반환하도록 하는 경우, 구매자;
(vii) 적법하게 권한을 부여받은 제3자(사법당국, 경찰, 샤카라카에 적용되는 조세의무상 필요한 경우 조세당국 등 포함)
- 제3자 제공 업무 발생 시, 필요시점에 고객에게 별도로 알리고 동의받는 절차가 진행됩니다.
- 제3자 제공 동의를 거부하실 수 있으며, 동의 거부는 회원가입과 서비스 사용에 영향을 미치지 않습니다.
- 샤카라카가 제공하는 서비스를 통해 거래가 성사되었을 때에는 쌍방 당사자간의 물품거래와 배송과 관련한 정보를 필요한 범위 내에서 쌍방당사자에게 제공합니다.
- 개인정보 제공 현황은 제휴관계의 개시 또는 종료에 따라 변동될 수 있으며 개인정보 제공과 함께 목록에 게시됩니다. 제휴관계가 종료되면 개인정보의 파기와 함께 목록에서 삭제 됩니다.
- 재난, 감염병, 급박한 생명・신체 위험을 초래하는 사건사고, 급박한 재산 손실 등의 긴급상황이 발생하는 경우 정보주체의 동의 없이 관계기관에 개인정보를 제공할 수 있습니다.

## 5. 개인정보처리의 위탁

샤카라카는 보다 나은 서비스 제공, 고객편의 제공 등 원활한 업무 수행을 위하여 다음과 같이 개인정보 처리 업무를 외부 전문업체에 위탁하여 운영하고 있습니다.

수탁업체 리스트는 해당 서비스 변경 및 계약기간에 따라 변경될 수 있으며 변경 시 개인정보처리방침을 통해 공지합니다.

| 수탁업체 | 위탁내용 | 개인정보의 보유 및 이용기간 |
| --- | --- | --- |
| (주)다날 | 본인인증, 결제 (휴대폰, 무통장 입금, 계좌이체, 신용카드) 기타 결제수단, 환불 시스템 제공 및 결제도용 방지 | 회원탈퇴 또는 위탁계약 종료시 |
| (주)아임포트 | 상품 구매에 필요한 신용카드, 현금결제 등의 결제정보 전송 | 회원탈퇴 또는 위탁계약 종료시 |
| Amazon Web Service, Inc | AWS를 통한 시스템 관리 | 회원탈퇴 또는 위탁계약 종료시 |
| 네이버클라우드(주) | 서비스 제공을 위한 시스템 개발 및 운영 | 회원탈퇴 또는 위탁계약 종료시 |
| 카카오페이 | 결제 및 환불 시스템 제공 | 회원탈퇴 또는 위탁계약 종료시 |

개인정보 처리의 해외 위탁

회사는 데이터 분석과 데이터 유실에 대비한 분산 저장을 위해 사용자의 개인 정보를 해외 클라우드에 위탁하고 있습니다.

| 구분 | Google Cloud Platform | Amazon Web Service, Inc |
| --- | --- | --- |
| 위탁업무 및 목적 | Google Cloud Storage에 데이터 저장 및 Google BigQuery를 통한 데이터 분석 | Simple Storage Service를 이용한 데이터 저장 |
| 수탁업체 연락처 | Tel) 080-822-1422 | Tel) 82-2-1544-8667 |
| 개인정보 이전국가 | 미국 | 미국 |
| 이전되는 개인정보 항목 | 수집하는 모든 개인정보 | 수집하는 모든 개인정보 |
| 개인정보 이전일시 | 데이터 수집 후 수 분 이내 | 데이터 수집 후 수 분 이내 |
| 개인정보 이전방법 | Google 클라우드 컴퓨팅 환경에 개인정보 보관 | Amazon 클라우드 컴퓨팅 환경에 개인정보 보관 |
| 보유 및 이용기간 | 회원탈퇴 또는 위탁계약 종료 시 | 회원탈퇴 또는 위탁계약 종료 시 |

## 6. 개인정보보호규정에 따른 이용자의 권리∙의무 및 행사방법

제1항 “개인정보의 수집 및 처리 목적”에 명시된 목적을 위해 이용자의 개인정보가 컨트롤러 자격의 샤카라카의 책임 하에 자동으로 처리됨을 알려드립니다.
개인정보보호법(총칭하여 “본건 규정”이라 함)의 규정을 준수하여, 귀하는 귀하의 권리를 고지받았음을 확인하며, 그에 따라 다음과 같은 권리가 부여됩니다:

- 이용자의 개인정보를 수정, 완료 또는 업데이트할 수 있는 접근 및 정정 권리;
- 부정확하거나, 불완전하거나, 모호하거나 진부화된 정보, 또는 수집, 사용, 공개 또는 보관이 금지된 정보를 삭제할 권리;
- 정당한 근거에 따라 이용자의 정보에 대한 처리를 반대할 권리;
- 이유를 불문하고, 예측분석(prospection)을 위한 이용자의 정보 사용에 반대할 권리;
- 이용자의 생애 이후 이용자의 개인정보 이용에 관한 가이드라인을 정의할 권리;
- 컴퓨터로 처리할 수 있는 일반적으로 사용되는 구조화된 포맷의 개인정보 이동성(data portability)에 대한 권리; 다만, 이 권리는 (i) 이용자가 제공한 귀하에 관한 정보 및 (ii) 자동화된 처리작업과 관련하여서만 행사될 수 있습니다;
- 이용자는 언제든지 이용자 개인정보를 조회하거나 수정할 수 있으며 수집·이용에 대한 동의 철회 또는 가입 해지를 요청할 수도 있습니다. 이용자의 권리를 행사하기 위해서, 다음 주소의 이메일로 정보보호관리자에게 문의하시기 바랍니다:  jh.shin@owlsociety.kr 이메일 문의 시, 관련 규정을 준수하여 이용자의 서명, 신원증명서의 사본, 회신용 주소를 포함하여야 하며, 민감한 개인정보는 신중히합니다.

## 7. 수집 및 처리할 수 있는 개인정보

이용자가 서비스 이용과정이나 개인정보 처리과정에서 샤카라카는 다음과 같은 정보를 포함하여 개인정보로 간주될 수 있는 특정정보들을 자동생성/ 수집 및 처리할 수 있습니다. 

이메일 주소, 직위, 성명, 닉네임(사용자명), 암호화된 이용자 확인값(CI), 중복가입확인정보(DI), 국가, 내외국인여부, 생년월일, 비밀번호, 프로필 이미지, 우편주소, 전화번호(들), IP 주소, 연결 및 브라우징 정보, 주문/판매/거래내역, 서비스이용내역, 지급, 클레임, 사고, 배송 관련 정보, 웹사이트 상 연락정보 및(해당되는 경우) 회사 상호, 브랜드명, 사업자등록정보, 은행정보, 샤카라카에서 공유되는 모든 콘텐츠(댓글, 메시지 등). 

특정 정보는 사이트에서의 귀하의 행동을 통해 자동적으로 수집됩니다(쿠키 섹션 참조)

서비스 이용에 반드시 필요한 개인정보를 다음과 같은 목적을 위하여 처리합니다. 처리하는 개인정보는 다음의 목적 이외의 용도로는 처리되지 않으며, 처리 목적과 항목이 변경되는 경우에는 필요한 조치를 이행할 것입니다.

| 서비스 | 항목 | 보유 및 이용기간 |
| --- | --- | --- |
| 회원가입 | 필수 : 휴대전화번호, 닉네임
선택 : 이름, 프로필 사진, 연령, SNS ID, 성별 | 동의 철회 또는 회원탈퇴시 |
| 샤카라카 내 구매 | 필수 : 배송주소, 이름, 카드사 명, 휴대전화번호 | 동의 철회 또는 회원탈퇴시 |
| 샤카라카 내 판매, 정산 | 필수 : 은행명, 계좌번호, 이름 | 동의 철회 또는 회원탈퇴시 |
| 고객 맞춤형 피드 | 선택 : 성별, 출생년도, SNS ID | 동의 철회 또는 회원탈퇴시 |
| 샤카라카 댓글 이용 시 | 앱 내 댓글 기능을 사용한 커뮤니케이션 내용 | 동의 철회 또는 회원탈퇴시 |
| 고객센터(상담) | <문의하기/신고하기>
- 전화고객센터 : 발신전화번호
- 웹고객센터 : 이메일, 휴대전화번호
※ 이메일 접수 시 답변받을 이메일 주소를 수집

<명예훼손/개인정보침해/저작권침해 신고>
- 게시물 차단 신청서 : 신청인정보(이름, 생년월일, 전화번호, 이메일 또는 팩스번호, 닉네임, 마스킹된 신분증 사본)
- (대리인 신고 시) 위임장 : 위임인/수임인정보(이름, 생년월일, 전화번호, 마스킹된 신분증 사본)

<명예훼손/개인정보침해에 대한 소명 요청>
- 게시물 복원 신청서 : 이름, 생년월일, 전화번호 또는 팩스번호, 이메일, 닉네임

<저작권침해에 대한 소명 요청>
- 복제,전송 재개 요청서 : 요청자/대리인 정보(이름, 생년월일, 전화번호, 이메일, 마스킹된 신분증 사본)

<자기게시물 접근배제 요청 시>
- (본인 신청 시) 자기게시물 접근배제 요청서 : 요청인 정보(이름, 전화번호, 이메일 또는 팩스번호, 마스킹된 신분증 사본), 자기게시물 입증자료에 포함된 개인정보
- 요청인 정보(이름, 전화번호, 이메일 또는 팩스번호, 마스킹 된 신분증 사본), 지정인 정보(이름, 전화번호, 이메일 또는 팩스번호, 신청인과의 관계, 마스킹 된 신분증 사본, 관계증빙서류-가족관계 증명서, 사망사실 증명서 등)

※ 문의/신고 유형에 따라 휴대전화번호 등 이용자가 추가로 입력하는 개인정보가 있을 수 있음 | 동의 철회 또는 회원탈퇴시 |
- 서비스 이용내역, 결제내역, 불량이용기록, 접속로그 : 서비스 제공 관련 고객상담 등의 목적
- 휴대폰 모델명 : 통계 및 서비스 품질 향상을 위한 목적
- 휴대폰 기기고유번호 : 사용자 기기 구별 목적으로 암호화된 정보를 수집
- 접속 IP 주소 : 의심거래 이용자에 대한 수사협조 목적
- 광고식별자, 단말기OS정보 : 마케팅 프로모션 시 사용자 및 기기 구분, 맞춤형 광고 서비스
- 위치정보 : 위치기반서비스 이용약관에 명시된 목적과 부정이용 방지 목적 
(위치기반서비스는 단말기의 설정을 변경함으로써 위치정보의 수집을 차단할 수 있습니다.)
- 은행정보 및 주문 분석 시스템(사기 탐지)
원칙적으로, 이용자의 은행정보는 결제 중에만 저장됩니다. 그러나,이용자의 동의를 전제로 하여, 향후 주문을 위해 정보를 다시 입력해야 하는 것을 피하기 위해 보안저장될 수 있으며, 그에 따라 향후 판매 및 구매를 용이하게 할 수 있습니다. 이 경우, 동의를 철회할 때까지 및/또는 이용자 신용카드의 만료 시까지 해당 정보가 저장됩니다. 이용자의 주문에 관한 은행정보는 당사의 결제파트너사에 의해 자동으로 처리됩니다. 이러한 자동화된 정보 처리의 목적은 주문을 결제하는 사람의 인증을 가능하게 하고 결제사기를 방지하기 위한 것입니다.
신용카드 부정 사용으로 인해 결제가 이루어지지 않으면 상기 서비스 제공자들뿐 아니라 내부 결제 사고 파일에도 이러한 미결제와 관련하여 주문과 관련된 연락처 정보가 등록됩니다. 허위 확인이나 오류는 특별 처리(사기 탐지 목적 등)를 초래할 수도 있습니다.

## 8. 개인정보 수집 방법

앞에서 언급된 바와 같이, 당사는 구체적으로 다음과 같은 때에 상기 제6항에 기재된 정보를 수집합니다.

- 이용자가 이 웹사이트에서 고객 프로필을 생성하는 경우;
- 이용자가 이 웹사이트에서 상품을 구매하거나 판매하는 경우;
- 이용자가 이 웹사이트를 탐색하고 상품을 보는 경우;
- 이용자가 추첨이나 콘테스트에 참여하는 경우;
- 이용자가 제공된 연락정보를 통해 직접적으로 및/또는 당사의 고객서비스부를 통해 당사에
연락하는 경우;
- 이용자가 당사와 계약을 체결하는 경우;
- 이용자가 당사의 메일링 목록(“뉴스레터”)를 구독하는 경우;
- 이용자가 이 웹사이트의 다른 이용자들과 교류하기 위해 귀하에게 제공되는 메시지 툴(“댓글”,”채
팅”)을 이용하는 경우;
- 이용자가 특정 쿠키의 설치를 수락하는 경우;
- 귀하가 특정 기간 후에 콘텐츠나 서비스에 다시 로그인하도록 요청받는 등의 경우에 보안조치를 이행하기 위한 목적;
- 내비게이션 문제를 발견하고, 이에 따라 당사 서비스의 사용성을 개선하기 위한 목적;
- 거래를 보호하고 사기를 차단하기 위한 목적(결제사기, 신원도용 등의 방지);
통신 서비스에 기여하는 장비의 부하를 조정하기 위한 목적;

## 9. 개인정보 보존, 파기절차 및 방법

이용자의 개인정보는 이용자로부터 동의를 받은 수집 및 이용목적이 달성된 때에는 회사 내부 방침 또는 관계 법령에서 정한 일정한 기간 동안 보관한 다음 파기합니다. 종이에 출력된 개인정보는 분쇄기로 분쇄하거나 소각을 통하여 파기하고, 전자적 파일 형태로 저장된 개인정보는 기록을 재생할 수 없는 기술적 방법을 사용하여 삭제합니다. 

샤카라카가 내부 방침 또는 법령에 따라 보관하는 개인정보 및 해당 법령은 아래 표와 같습니다. 또한 아래 기준과 별개로 회사는 1년간 서비스를 이용하지 않은 회원의 개인정보를 별도로 분리 보관 또는 삭제하고 있으며, 분리 보관된 개인정보는 4년간 보관 후 지체없이 파기합니다. '이용목적이 달성된 때'란 철회요청, 서비스계약 만료, 탈퇴 시를 의미합니다.

| 보존항목 | 보존 이유 | 보존 기간 |
| --- | --- | --- |
| 부정 이용 기록 (신고, 제재) | 부정 이용 방지 | 10년 |
| 판매 게시물 및 채팅 내용 | 거래 관련 사기 방지 및 분쟁 해결 | 5년 |
| 탈퇴한 이용자의 휴대전화번호 | 부정 가입 및 이용 방지 | (탈퇴일로부터) 1년 |

| 보존항목 | 근거 법령 | 보존 기간 |
| --- | --- | --- |
| 계약 또는 청약철회 등에 관한 기록 | 전자상거래 등에서의 소비자보호에 관한 법률 | 5년 |
| 대금결제 및 재화 등의 공급에 관한 기록 | 전자상거래 등에서의 소비자보호에 관한 법률 | 5년 |
| 소비자의 불만 또는 분쟁처리에 관한 기록 | 전자상거래 등에서의 소비자보호에 관한 법률 | 3년 |
| 표시/광고에 관한 기록 | 전자상거래 등에서의 소비자보호에 관한 법률 | 6개월 |
| 세법이 규정하는 모든 거래에 관한 장부 및 증빙서류 | 국세기본법 | 5년 |
| 전자금융 거래에 관한 기록 | 전자금융거래법 | 5년 |
| 서비스 방문기록 | 통신비밀보호법 | 3개월 |

## 10. 쿠키

1) 쿠키의 정의

쿠키의 정의 쿠키는 서버가 이용자의 단말기(컴퓨터, 전화기 등)에 저장하고 웹 도메인(즉, 대부분의 경우 단일 웹사이트의 모든 페이지)과 관련되는 작은 파일입니다.

일반적으로, 쿠키는 직·간접적으로 식별하는 특정 정보(쿠키를 저장한 서버명, 세션 식별자, 만료일, 또는 이용자가 방문한 페이지 등과 같이 이용자가 방문하고 있는 사이트 내의 내비게이션에 대한 정보(단말기 식별자, 광고 식별자 등)를 포함합니다. 쿠키는 다양한 용도를 가지고 있습니다:

쿠키는 이용자의 광고 ID, 이용자의 장바구니 내용, 웹페이지가 표시되는 언어를 기억하고, 통계 또는 광고 목적상 이용자의 브라우징을 추적하고 콘텐츠를 개인화하는 등의 용도로 사용될 수 있습니다. 간단히 말하자면, 당사는 쿠키라는 용어를 모든 유형의 추적자(tracer)를 지칭하는 데 사용합니다.

2) 단말기에 쿠키를 배치할 수 있는 자

-> 샤카라카 -> 샤카라카의 파트너: 제3자 회사(광고 대행사, 사기방지 서비스제공자나 당사 파트너의 고객 등)입니다. 파트너 목록을 참조하시기 바랍니다(또한, 쿠키관리 플랫폼을 통해 확 인할 수 있습니다). 이용자가 당사의 플랫폼을 탐색하거나 당사의 플랫폼이나 파트너 사이트에서 당사의 광고란을 클릭하는 경우, 쿠키가 저장될 수 있습니다.

3) 쿠키를 설정하는 방법

  3.1) 샤카라카 배너 이용자가 플랫폼에 처음 접속하는 경우, 사용되는 쿠키의 카테고리에 대해 이용자에게 고지하고  (i) 이용행태 측정, (ii) 콘텐츠 맞춤화, (iii) 개인맞춤형 광고 및 (iv) 소셜 네트워크상 공유와 관련된 쿠키를 수락 또는 거부할 기회를 이용자에게 제공하기 위한 배너가 표출됩니다. 

이용자가 선택을 완료한 경우, 이를 6개월 동안 기억하기 위해 필수 쿠키가 이용자의 단말기에 배치 됩니다. 이는 이용자가 당사의 플랫폼에 방문할 때마다 이용자에게 쿠키를 설정할 것을 묻는 것을 방지합니다. 

이용자가 이후 쿠키 설정을 변경하고자 하는 경우, 이용자는 배너가 다시 표출되도록 하기 위해 이용자의 브라우저 쿠키를 삭제하거나 쿠키관리 플랫폼에 접근해야 합니다.

 3.2) 이용자의 브라우저 또는 단말기 설정 또한, 이용자는 관련 웹사이트나 애플리케이션을 불문하고 쿠키를 일반적으로 관리하기 위해 이용자의 브라우저나 단말기를 설정할 수 있습니다. 각 브라우저의 구성은 상이합니다. 이는 쿠키 와 관련된 이용자의 요구를 수정할 방법을 알려줄 수 있는 이용자의 브라우저의 도움 메뉴에 명시 되어 있습니다. 쿠키를 거부하거나 삭제하는 경우, 해당 사이트가 적절하게 작동하는 것을 방해할 수 있습니다. 

4) 샤카라카에서 사용되는 쿠키 및 처리 

 4.1) 필수 쿠키(설정 불가) 이 쿠키는 디폴트로 설치됩니다: 이용자는 플랫폼을 통해 이 쿠키를 설정할 수 없습니다. 이 쿠키는 당사의 플랫폼 운영을 위해 필요하거나, 서비스 요청에 해당하는 이용자의 행동에 당사 측이 응함으로써 설치됩니다. 이 쿠키는 플랫폼이 가능한 적절하게 기능할 수 있도록 하고 그 보안을 보장하기 위한 샤카라카의 적법한 이익을 기반으로 합니다.  

보유기간: 쿠키의 수명은 일반적으로 세션 기간이며, 최대 수개월에 이릅니다. 

차단 시 결과: 이용자는 더 이상 플랫폼 및/또는 플랫폼의 서비스에 접근하지 못할 수 있습니다.

세부 목적: 

- 추적자의 저장에 대한 이용자의 명시적인 선택을 보유하기 위한 목적;
- 이용자가 플랫폼의 한 페이지에서 다른 페이지로 이동하는 경우, 이용자가 연결 상태를 유지하기 위한 목적;
- 이용자의 장바구니 내용을 계속 추적하거나 구매한 상품(들) 및/또는 서비스(들)에 대한 대금을 청구하기 위한 목적;
- 이용자 인터페이스를 맞춤화하기 위한 목적(언어 선택이나 당사의 서비스 제공 등); 당사의 플랫폼을 정확하게 구현하기 위해 이용자의 화면과 윈도우의 형식 및 크기를 저장하기 위한 목 적;
- 로봇이나 예기치 못한 접속 시도를 제한하는 등의 행위를 통해 서비스에 이용자를 인증하기 위 한 목적(인증 메커니즘의 보안을 보장하기 위한 목표 포함);
- 당사가 다수의 가짜 계정을 생성하기 위해 사용된 컴퓨터를 식별하도록 하여 스팸 및 피싱 시 도를 차단하기 위한 목적;
- 멀웨어(malware)에 감염된 컴퓨터를 감지하고 손해를 초래하지 않도록 방지하기 위한 목적
- 이용자가 특정 기간 후에 콘텐츠나 서비스에 다시 로그인하도록 요청받는 등의 경우에 보안조치를 이행하기 위한 목적;
- 내비게이션 문제를 발견하고, 이에 따라 당사 서비스의 사용성을 개선하기 위한 목적;
- 거래를 보호하고 사기를 차단하기 위한 목적(결제사기, 신원도용 등의 방지);
- 통신 서비스에 기여하는 장비의 부하를 조정하기 위한 목적;
- 서버 간의 트래픽을 전송하고 당사 페이지의 로딩 속도를 파악하기 위한 목적;
- 플랫폼을 통해 커뮤니케이션을 원활하게 하기 위한 목적;
- 잠재고객, 트래픽을 측정하고 당사의 플랫폼과 그 서비스에 대한 통계를 수행하며, 플랫폼의 기능 등을 개선하기 위한 목적.

4.2) 잠재고객 쿠키(이용자의 사전 동의를 받아야 함): 

이 쿠키는 이용자의 수와 이용자가 당사의 플랫폼을 탐색하는 방법에 대한 통계분석을 종합적으로 수행하기 위해 당사가 이용자의 사이트 이용에 대한 정보(방문횟수, 방문한 페이지, 사이트에 머문 시간 등)를 수집할 수 있도록 합니다. 

보유기간: 이 쿠키의 수명은 13개월을 초과하지 않습니다. 

차단 시 결과: 이용자에 대해 어떠한 결과도 초래하지 않으나, 쿠키를 수락함으로써 이용자는 당사의 플랫폼과 당사의 서비스의 개선에 기여합니다. 

구글을 통해 직접 이 쿠키를 차단하는 방법: Google 애널리틱스 차단 브라우저 부가기능 – 애널리틱스 고객센터 

세부 목적: 

- 잠재고객, 트래픽 및 당사의 플랫폼과 그 서비스의 성과를 측정하고 플랫폼의 기능을 개선하기 위한 목적;
- 보다 심층적으로 트래픽을 분석하기 위한 목적

4.3) 콘텐츠 개인화 쿠키(이용자의 사전 동의를 받아야 함): 

이 쿠키는 직접적으로 개인정보를 저장하지 않으나, 이용자의 브라우저 및 단말기의 고유한 식별을 기반으로 합니다. 이 쿠키는 당사가 이용자의 이용자 프로필을 추정하고 이용자의 선호에 가장 적합한 상품, 서비스 및 콘텐츠를 추천하며 당사의 플랫폼에서 이용자에게 특정 프로모션용 제안 을 제공할 수 있도록 합니다. 

보유기간: 쿠키의 수명은 매우 짧으며, 일반적으로 세션 기간은 몇 시간에서 최대 13개월입니다. 

차단 시 결과: 플랫폼 이용자에 대해 어떠한 결과도 초래하지 않습니다. 반면, 

쿠키를 삭제하는 경우, 이용자의 관심사를 고려하지 않은 콘텐츠를 보여주는 결과를 초래합니다. 이 쿠키를 수락 함으로써, 이용자는 풍부하고 개인화된 경험을 얻을 수 있습니다

세부 목적: 

- 이용자의 선호, 관심 및 이용자의 플랫폼 브라우징 및 사용에 따라 개인화된 콘텐츠, 상품 및 서비스를 이용자에게 제공하기 위한 목적;
- 이용자의 당사의 플랫폼 방문을 식별하고, 이용자가 방문하는 페이지와 이용자가 클릭한 링크를 추적하며, 당사의 플랫폼 및 이에 표출된 광고를 이용자의 관심에 보다 맞춤화 하기 위해 이용자의 이 용자 프로필을 알기 위한 목적. 당사가 광고 메일(뉴스레터 가입 등)이나 푸시 알림을 이용자에게 전송할 수 있도록 허용한 경우, 당사는 또한 이를 개인화하기 위해 이러한 정보를 사용합니다(타겟팅 또는 광고 쿠키).

4.4) 개인화 광고 쿠키(이용자의 사전 동의를 받아야 함): 

이 쿠키는 당사의 광고 파트너가 당사 플랫폼 내에 설정할 수 있습니다. 이 쿠키는 해당 회사가 이용자의 관심사를 파악하고 다른 웹사이트에서 관련성 있는 개인화된 광고를 제공하기 위해 사 용할 수 있습니다. 이 쿠키는 당사가 이용자의 선호에 가장 잘 맞는 광고를 표출할 수 있도록 합니다. 또한, 이용자의 프로필은 당사의 파트너 또는 이러한 파트너의 고객인 다른 퍼블리셔와 이용자의 인터랙션으로 부터 취득한 정보로 축적됩니다. 

보유기간: 쿠키의 수명은 매우 짧으며, 일반적으로 세션 기간은 몇 시간에서 최대 13개월입니다. 

차단 시 결과: 플랫폼 이용자에 대해 어떠한 결과도 초래하지 않습니다. 반면, 쿠키를 삭제하더라도 인터넷상 광고의 중단으로 이어지지 않습니다. 다만, 이용자의 관심을 고려하지 않은 광고 를 표출하게 됩니다. 이 쿠키를 수락함으로써, 이용자는 풍부하고 개인화된 경험을 얻을 수 있습니다. 

세부 목적: 

- 이용자의 선호, 관심 및 이용자의 플랫폼 브라우징 및 사용에 따라 개인화된 콘텐츠, 상품 및 서비스를 이용자에게 제공하기 위한 목적;
- 당사의 광고 공간에 당사가 게시한 총 광고 수를 계산하고, 이러한 광고, 각 광고의 표출 수, 각 광고를 클릭한 이용자 수 및 (해당되는 경우) 이러한 광고가 유도하는 페이지에서 이용자들 이 취한 후속 행동을 식별하기 위한 목적;
- 당사 플랫폼 또는 제3자 사이트의 내비게이션, 이용자의 구매 및/또는 (이용자의 사전 동의를 받아) 이용자의 단말기가 전송한 위치정보(경도 및 위도)에 따라 당사의 광고 공간 및 당사의 오퍼를 통해 당사 플랫폼의 광고 콘텐츠를 조정하기 위한 목적;
- 예를 들어, 이용자에게 전자적 프로스펙팅을 보내기 위해, 또는 당사가 발행하는 광고 공간내에서, 이용자를 위해 특별히 의도한 것으로서 개인적으로 이용자의 흥미를 유발할 수 있는 개인화 된 광고를 이용자의 단말기에 표출하기 위해 이 정보를 브라우징 정보와 연결하기 위한 목적.

4.5) 소셜 네트워크 공유 쿠키(이용자의 동의를 받아야 함): 

샤카라카에는 제3자의 컴퓨터 애플리케이션이 포함될 수 있으며, 이를 통해 이용자는 당사 사이트의 콘텐츠를 다른 사람들과 공유하거나 다른 사람들에게 당사의 플랫폼 콘텐츠에 관한 귀하의 조언 또는 의견을 알릴 수 있습니다. 이는 “페이스북”과 같은 소셜 네트워크의 “공유”와 “좋아요” 버튼의 경우입니다. 이러한 애플리케이션 버튼을 제공하는 소셜 네트워크는 당사 사이트를 방문할 때 이 버튼을 사용하지 않았더라도 이 버튼 덕분에 이용자를 알아볼 수 있습니다. 실제로, 이러한 유형의 애플리케이션 버튼을 사용하면 당사 사이트를 브라우징하는 동안 관련 소셜 네트워크의 이용자의 계정 이 이용자의 단말기에서 활성화되기(공개 세션) 때문에 해당 소셜 네트워크가 이용자의 당사 사이트 브라우징을 추적할 수 있습니다. 

당사는 이러한 제3자들이 이용자의 당사 사이트 브라우징 및 제3자들이 보유한 개인정보와 관련 된 정보를 수집하기 위해 사용하는 프로세스에 대해 통제하지 않습니다. 특히 광고 목적으로이러한 애플리케이션 버튼을 통해 위의 제3자들이 수집하는 브라우징 정보의 사용 목적에 대 해 알아보려면 이러한 소셜 네트워크의 개인정보보호방침을 참조하기 바랍니다. 

이러한 보호 방침을 통해, 특히 각 네트워크에 대한 이용자의 이용자 계정을 구성하여 이러한 소셜 네트워크 에서 이용자의 선택사항을 실행할 수 있어야 합니다. 

보유기간: 공유 쿠키의 보유기간은 각 소셜 네트워크에 의해 결정되지만, 13개월을 초과할 수 없습니다. 

차단 시 결과: 플랫폼 이용에 대해 어떠한 결과도 초래하지 않습니다. 다만, 이 쿠키를 비활성화 하면 관련 네트워크와의 인터랙션이 차단됩니다.

## 11. 개인정보의 안전성 확보조치

샤카라카는 개인정보 보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 기술적/관리적 및 물리적 조치를 하고 있습니다.

- 개인정보 취급자의 최소화 및 교육

개인정보를 처리하는 직원을 최소화 하며, 개인정보를 처리하는 모든 임직원들을 대상으로 개인정보보호 의무와 보안에 대한 정기적인 교육과 캠페인을 실시하고 있습니다.

- 개인정보에 대한 접근 제한

개인정보를 처리하는 시스템에 대한 접근권한의 부여, 변경, 말소를 통하여 개인정보에 대한 접근통제를 위하여 필요한 조치를 하고 있으며 침입차단시스템을 이용하여 외부로부터의 무단 접근을 통제하고 있습니다.

- 접속기록의 보관 및 위변조 방지

개인정보처리시스템에 접속한 기록(웹 로그, 요약정보 등)을 최소 2년 이상 보관, 관리하고 있으며, 접속 기록이 위변조 및 도난, 분실되지 않도록 보안기능을 사용하고 있습니다.

- 개인정보의 암호화

신용카드 번호 등 중요정보는 암호화 되어 저장 및 관리되고 있습니다. 또한 중요한 데이터는 저장 및 전송 시 암호화하여 사용하는 등의 별도 보안기능을 사용하고 있습니다.

- 해킹 등에 대비한 기술적 대책

해킹이나 컴퓨터 바이러스 등에 의한 개인정보 유출 및 훼손을 막기 위하여 보안프로그램을 설치하고 주기적인 갱신·점검을 하며 외부로부터 접근이 통제된 구역에 시스템을 설치하고 기술적/물리적으로 감시 및 차단하고 있습니다.

## 12. 개인정보 보호책임자 지정

개인정보보호 책임자

사용자가 서비스를 이용하면서 발생하는 모든 개인정보보호 관련 문의, 불만, 조언이나 기타 사항은 개인정보 보호책임자 및 담당부서로 연락해 주시기 바랍니다. 샤카라카는 사용자 목소리에 귀 기울이고 신속하고 충분한 답변을 드릴 수 있도록 최선을 다하겠습니다.

- 이름: 신지희
- 직위: 부대표/이사
- 부서: 총괄
- 연락처: hello@owlsociety.kr / 02-6085-8855

## 13. 권익침해 구제방법

아래의 기관은 별개의 기관으로서, 샤카라카의 자체적인 개인정보 불만처리, 피해구제 결과에 만족하지 못하시거나 보다 자세한 도움이 필요하시면 문의하여 주시기 바랍니다.

1) 개인정보 침해신고센터 (한국인터넷진흥원 운영)

- 소관업무 : 개인정보 침해사실 신고, 상담 신청
- 홈페이지 : [http://privacy.kisa.or.kr](http://privacy.kisa.or.kr/)
- 전화 : (국번없이) 118
- 주소 : (58324) 전남 나주시 진흥길 9 3층 개인정보침해신고센터

2) 개인정보 분쟁조정위원회

- 소관업무 : 개인정보 분쟁조정신청, 집단분쟁조정 (민사적 해결)
- 홈페이지 : [http://www.kopico.go.kr](http://www.kopico.go.kr/)
- 전화 : (국번없이) 1833-6972
- 주소 : (03171) 서울특별시 종로구 세종대로 209 정부서울청사 4층

3) 대검찰청 사이버범죄 수사단 : 02-3480-3573, [www.spo.go.kr](http://www.spo.go.kr/)

4) 경찰청 사이버안전국 : (국번없이) 182, [http://cyberbureau.police.go.kr](http://cyberbureau.police.go.kr/)

## 14. 인터넷 접속정보파일 등 개인정보를 자동으로 수집하는 장치의 설치,운영 및 그 거부에 관한 사항

샤카라카는 개별적인 맞춤서비스를 제공하기 위해 이용정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용합니다.

쿠키는 웹사이트를 운영하는데 이용되는 서버(https)가 이용자의 컴퓨터 브라우저에게 보내는 소량의 정보이며 이용자들의 디바이스 내에 저장되기도 합니다. 이용자가 웹사이트에 접속을 하면 콜렉티브의 서버는 이용자들의 쿠키의 내용을 읽고, 이용자의 추가정보를 귀하의 컴퓨터에서 찾아 접속에 따른 성명 등의 추가 입력 없이 서비스를 제공할 수 있습니다.

쿠키는 이용자의 컴퓨터는 식별하지만 귀하를 개인적으로 식별하지는 않습니다. 또한, 이용자는 쿠키 설치에 대한 선택권을 가지고 있습니다. 따라서, 이용자는 웹 브라우저에서 옵션을 설정함으로써 모든 쿠키를 허용하거나, 쿠키가 저장될 때마다 확인을 거치거나, 모든 쿠키의 저장을 거부할 수도 있습니다. 다만, 쿠키 설치를 거부할 경우 웹 사용이 불편해지며 로그인이 필요한 일부 서비스 이용에 어려움이 있을 수 있습니다.

- 설정 방법의 예
(i) Internet Explorer의 경우 웹 브라우저 상단의 도구 메뉴 > 인터넷 옵션 > 개인정보 > 설정
(ii) Chrome의 경우 웹 브라우저 우측의 설정 메뉴 > 화면 하단의 고급 설정 표시 > 개인정보의 콘텐츠 설정 버튼 > 쿠키

## 15. 광고식별자 수집 및 온라인 맞춤형 광고

샤카라카는 이용자의 웹 또는 앱 이용형태, 접속기록 등을 분석하여 이용자의 특성을 고려한 맞춤형 광고 서비스를 제공하고 있으며 이러한 서비스를 제공하기 위하여 회사는 광고식별자(ADID, IDFA)를 수집할 수 있습니다.

광고식별자는 Android / Apple 광고용 ID 등과 같이 개인을 직접적으로 식별할 수 없는 값으로서 기기의 설정을 변경하여 광고식별자가 이용자의 관심사항이 기반하는 광고를 제공하는 목적으로 사용되지 못하도록 거부할 수 있으며 이러한 식별자는 개인을 식별하는 용도로 사용되지 않습니다.

1) 앱 광고식별자 수집거부 방법

- 안드로이드(Android)
 (i) 설정 ▷ 구글(구글설정) ▷ 광고 ▷ 광고 맞춤설정 선택 해제
 또는
 (ii)설정 ▷ 일반 ▷ 계정 및 동기화 ▷ 구글(구글설정) ▷ 개인정보 및 개인정보보호 ▷ 광고설정
- 아이폰(iOS)
 (i) 설정 ▷ 개인정보보호 ▷ 광고 ▷ 광고 추적 제한

2) 앱 광고식별자 수집 및 처리

- 광고식별자 처리 사업자 : Google, FaceBook, Appsflyer, Moloco, 매드잇
- 광고식별자 수집방법 : 이용자가 앱을 실행할 때 자동수집 및 전송

## 16. 개인정보 처리방침 변경

샤카라카는 법률이나 서비스의 변경사항을 반영하기 위한 목적 등으로 개인정보처리방침을 수정할 수 있습니다. 개인정보처리방침이 변경되는 경우 샤카라카는 변경 사항을 게시하며, 변경된 개인정보처리방침은 게시한 날로부터 7일 후부터 효력이 발생합니다.

공지일자 : 2022년 11월 11일

## 17. 추가 문의

샤카라카가 이용자의 개인정보를 수집 및 처리하는 방법에 대해 추가 문의 사항이 있는 경우,  고객센터나 이메일jh.shin@owlsociety.kr 로 보내 주시면 답변해드리겠습니다. 

문의 시, 민감한 개인정보는 포함되지 않도록 주의 바랍니다.  

기타 문의는 고객센터 : <u>[카카오톡 플러스친구 (@샤카라카)](http://pf.kakao.com/_MiAfxj/chat)</u>를 이용해주세요.

`;

const PrivacyPolicy = () => {
  return (
    <Container>
      <BackHeader title="개인정보 처리방침" />
      <ContentWrapper>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {privacyPolicyMarkdown}
        </ReactMarkdown>
      </ContentWrapper>
    </Container>
  );
};

export default PrivacyPolicy;
